import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import MenuItem from '@mui/material/MenuItem';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useConfig } from '../config';
import { logout as logoutRoute, profile, resolvePath, sso as ssoPath, useNavigate, useQuery, welcome } from '../routes';
import { fromAuth, fromOrganizationFeatureFlag, fromSubscriptions, fromTestOpsPlatformSubscriptions } from '../store';
import { ReactComponent as SignOutIcon } from './icons/SignOutIcon.svg';
import MenuListDecorator from './MenuListDecorator';
import { useLaunchDarkly } from '../launchdarkly';

import {
  BuildingIcon,
  CommunityIcon,
  DocumentationIcon,
  SubmitTicketIcon,
  ViewProfileIcon,
  UserSettingsIcon,
} from './CustomIcon';
import { Organization, OrganizationFeature } from '../models';

const useStyles = makeStyles(theme => ({
  settingButton: {
    height: '100%',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  itemIcon: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    minWidth: theme.spacing(5),
  },
  verticalBox: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    width: theme.spacing(35),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
    wordBreak: 'break-word',
  },
  userSettingAvatar: {
    cursor: 'pointer',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  userAvatar: {
    minWidth: theme.spacing(6),
    minHeight: theme.spacing(6),
  },
  viewProfileButton: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  menuItem: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  menuContainer: {
    maxHeight: '90vh',
    overflow: 'auto',
  },
  actionIcon: {
    width: theme.spacing(1.75),
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
}));

export interface UserSettingMenuProps {
  organization?: Organization | null;
}

const getSubdomainLink = (domain: string | undefined) => `https://${domain}.${process.env.REACT_APP_CUSTOM_DOMAIN_POSTFIX}`;

const UserSettingMenu = (props: UserSettingMenuProps) => {
  const { organization } = props;
  const classes = useStyles();
  const user = useSelector(fromAuth.selectUser);
  const { replace, replaceQuery } = useNavigate();
  const { config } = useConfig();
  const { flags } = useLaunchDarkly();
  const orgId = Number(useQuery().get('orgId'));
  const accountId = Number(useQuery().get('accountId'));
  const { queryDictionary } = useQuery();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleClick = () => {
    setMenuOpen(!menuOpen);
  };

  const activePlatformSubscription = useSelector(fromTestOpsPlatformSubscriptions
    .selectPaidTestOpsPlatformSubscriptionByAccountId(
      accountId,
    )).filter(sub => new Date(sub.expiryDate).getTime() > Date.now())?.[0];

  const hasTOEnterprise = useSelector(fromSubscriptions
    .selectHasTOEnterpriseByOrganizationId(Number(orgId) || 0));

  const isSsoFlagEnabled = useSelector(fromOrganizationFeatureFlag.selectIsOrganizationSsoEnabled(
    Number(orgId),
  )) || false;

  const hasEnableSSOAndCustomDomain = (
    activePlatformSubscription?.feature === OrganizationFeature.TESTOPS_PLATFORM
    && activePlatformSubscription.quota >= 30000)
  || hasTOEnterprise
  || isSsoFlagEnabled;

  // handle click away or click on menu item
  const closeMenu = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      // Triggered by clickaway listener, but what we clicked is the avatar
      // so we let this event be handled by handleClick()
      return;
    }
    setMenuOpen(false);
  };

  const handleLogout = () => {
    replace(
      logoutRoute.path,
      replaceQuery(queryDictionary()),
    );
  };

  return (
    <div id="user_setting">
      {/* Zero-length titles string are never displayed */}
      <IconButton
        onClick={handleClick}
        ref={anchorRef}
        className={classes.settingButton}
        size="large"
      >
        <Avatar
          className={classes.userSettingAvatar}
          src={user?.avatar || 'https://katalon-testops.s3.amazonaws.com/image/icon/defaultAvatar.png'}
        >
          <PersonIcon />
        </Avatar>
      </IconButton>
      <MenuListDecorator
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClickAway={closeMenu}
      >
        <div className={classes.menuContainer}>
          <Box className={classes.verticalBox}>
            <Avatar
              className={classes.userAvatar}
              src={user?.avatar || 'https://katalon-testops.s3.amazonaws.com/image/icon/defaultAvatar.png'}
            >
              <PersonIcon />
            </Avatar>
            <Box fontWeight="fontWeightBold">
              {!user?.firstName && !user?.lastName ? user?.email.split('@')[0] : `${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
            </Box>
            <Typography color="textSecondary">
              {user?.email}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={closeMenu}
              component={RouterLink}
              to={config?.onpremise ? resolvePath(ssoPath, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/user/profile` }) : resolvePath(profile)}
              classes={{ containedSizeSmall: classes.viewProfileButton }}
            >
              <FormattedMessage id="layout.header.accountsetting.view_profile" />
              { flags?.enhanceUserSettingEnable
                ? <ViewProfileIcon className={classes.actionIcon} />
                : <></> }
            </Button>
          </Box>
          <Divider />
          <MenuItem
            onClick={closeMenu}
            className={classes.menuItem}
            component={RouterLink}
            to={organization?.domain && hasEnableSSOAndCustomDomain ? resolvePath(ssoPath, undefined, { redirect_uri: `${getSubdomainLink(organization?.domain)}/user/settings` }) : resolvePath(ssoPath, undefined, { redirect_uri: `${config?.testOpsPublicUrl}/user/settings` })}
            divider
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <UserSettingsIcon />
            </ListItemAvatar>
            {
              flags?.enhanceUserSettingEnable
                ? <FormattedMessage id="layout.header.accountsetting.user_settings" />
                : <FormattedMessage id="layout.header.accountsetting.notification_setting" />
            }
          </MenuItem>
          { /* <MenuItem
            component="a"
            className={classes.menuItem}
            onClick={closeMenu}
            target="_blank"
            rel="noopener noreferrer"
            href="https://github.com/katalon-studio/katalon-testops/issues/new?template=feature_request.md&title=[Feature]"
            divider
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <FeatureRequestIcon />
            </ListItemAvatar>
            <FormattedMessage id="layout.header.accountsetting.feature_request" />
          </MenuItem>
          <MenuItem
            component="a"
            className={classes.menuItem}
            onClick={closeMenu}
            target="_blank"
            rel="noopener noreferrer"
            href="https://github.com/katalon-studio/katalon-testops/issues/new?template=bug_report.md&title=[Bug]"
            divider
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <BugReportIcon />
            </ListItemAvatar>
            <FormattedMessage id="layout.header.accountsetting.bug_report" />
          </MenuItem> */ }
          <MenuItem
            component="a"
            className={classes.menuItem}
            onClick={closeMenu}
            target="_blank"
            rel="noopener noreferrer"
            href="https://docs.katalon.com/"
            divider
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <DocumentationIcon />
            </ListItemAvatar>
            <FormattedMessage id="layout.header.accountsetting.documentation" />
          </MenuItem>
          <MenuItem
            component="a"
            className={classes.menuItem}
            target="_blank"
            rel="noopener noreferrer"
            href="https://forum.katalon.com"
            onClick={closeMenu}
            divider
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <CommunityIcon />
            </ListItemAvatar>
            {
              flags?.enhanceUserSettingEnable
                ? <FormattedMessage id="layout.header.accountsetting.community_portal" />
                : <FormattedMessage id="layout.header.accountsetting.community" />
            }
          </MenuItem>
          {!config?.onpremise && config?.serviceCloudEnabled && (
            <MenuItem
              component="a"
              className={classes.menuItem}
              href={`${process.env.REACT_APP_SERVICE_CLOUD_OAUTH_INITIALIZATION_URL}`}
              onClick={closeMenu}
              divider
            >
              <ListItemAvatar classes={{ root: classes.itemIcon }}>
                <SubmitTicketIcon />
              </ListItemAvatar>
              <FormattedMessage id="layout.header.accountsetting.submit_a_support_case" />
            </MenuItem>
          )}
          <MenuItem
            className={classes.menuItem}
            onClick={closeMenu}
            component={RouterLink}
            to={resolvePath(welcome)}
            divider
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <BuildingIcon />
            </ListItemAvatar>
            <FormattedMessage id="layout.header.accountsetting.switch_account" />
          </MenuItem>
          <MenuItem
            className={classes.menuItem}
            onClick={handleLogout}
          >
            <ListItemAvatar classes={{ root: classes.itemIcon }}>
              <SignOutIcon />
            </ListItemAvatar>
            <FormattedMessage id="layout.header.accountsetting.sign_out" />
          </MenuItem>
        </div>
      </MenuListDecorator>
    </div>
  );
};

export default UserSettingMenu;
